import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SessionStore } from '@argo/session';
import { 
  ScaModuleTitleComponent,
  ScaCardWorkshopComponent,
  ScaAlertComponent,
  ScaBadgeComponent,
  ScaIconComponent,
  ScaViewMoreComponent
} from '@argo-dev-components-sca-angular/dev-components-sca';

@Component({
  selector: 'app-session-test',
  standalone: true,
  imports: [
    CommonModule,
    ScaModuleTitleComponent,
    ScaCardWorkshopComponent,
    ScaAlertComponent,
    ScaBadgeComponent,
    ScaIconComponent,
    ScaViewMoreComponent
  ],
  template: `
    <div class="container">
       <sca-module-title class="title-test">
        <h1>Session  Playground</h1>
        <span scaModuleTitlePost>Gestión de la sincronización de las sessiones entre proyectos - Testing @argo/seo</span>
      </sca-module-title>

      <!-- Estado del Store -->
      <section class="card">
        <h2>Estado del Store</h2>
        
        <sca-alert variant="info" style="margin-bottom: 1.5rem;">
          <sca-icon icon="info" size="24px" [weight]="300" left />
          SessionStore coordina la sesión entre múltiples pestañas del navegador
        </sca-alert>
        
        <div class="utilities-grid">
          <sca-card-workshop [selected]="true">
            <header scaCardWorkshopHeader>
              <sca-icon icon="sync" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>Fase Actual</p>
            <p scaCardWorkshopAddress>
              <sca-badge variant="accent">{{ sessionStore.phase$() }}</sca-badge>
            </p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="fingerprint" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>Instance ID</p>
            <p scaCardWorkshopAddress style="word-break: break-all; font-size: 0.85rem;">{{ sessionStore.instanceId$() }}</p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="security" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>Delegate Session</p>
            <p scaCardWorkshopAddress>
              <sca-badge [variant]="sessionStore.delegateSession$() ? 'success' : 'error'">
                {{ sessionStore.delegateSession$() ? 'ACTIVO' : 'DESACTIVADO' }}
              </sca-badge>
            </p>
          </sca-card-workshop>
        </div>
      </section>

      <!-- Funcionalidad -->
      <section class="card">
        <h2>Funcionalidad del SessionStore</h2>
        
        <sca-alert variant="info" style="margin-bottom: 1.5rem;">
          <sca-icon icon="info" size="24px" [weight]="300" left />
          Características principales de sincronización multi-tab
        </sca-alert>
        
        <div class="utilities-grid">
          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="sync_alt" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>Sincronización Multi-Tab</p>
            <p scaCardWorkshopAddress>Coordina el estado de autenticación entre pestañas abiertas</p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="cookie" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>Cookies de Coordinación</p>
            <p scaCardWorkshopAddress>Usa cookies (polling 1s) para detectar cambios de sesión entre tabs</p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="sensors" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>Eventos Observables</p>
            <p scaCardWorkshopAddress>Stream: Logout, TokenRenovated, TokenWillExpire</p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="verified_user" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>Integración SecurityStore</p>
            <p scaCardWorkshopAddress>Propaga eventos de autenticación a todas las pestañas activas</p>
          </sca-card-workshop>
        </div>
      </section>

      <!-- Eventos de Sincronización -->
      <section class="card">
        <h2>Eventos de Sincronización</h2>
        
        <sca-alert variant="info" style="margin-bottom: 1.5rem;">
          <sca-icon icon="info" size="24px" [weight]="300" left />
          Eventos que se propagan automáticamente entre pestañas
        </sca-alert>
        
        <div class="utilities-grid">
          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="logout" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>Logout</p>
            <p scaCardWorkshopAddress>
              <sca-badge variant="default">log_out</sca-badge><br>
              Se cierra sesión en una tab → todas las tabs cierran sesión
            </p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="refresh" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>TokenRenovated</p>
            <p scaCardWorkshopAddress>
              <sca-badge variant="default">token_renovated</sca-badge><br>
              Token renovado en una tab → otras tabs actualizan token
            </p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="schedule" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>TokenWillExpire</p>
            <p scaCardWorkshopAddress>
              <sca-badge variant="default">token_will_expire</sca-badge><br>
              Token próximo a expirar → avisar a todas las tabs
            </p>
          </sca-card-workshop>
        </div>
      </section>

      <!-- Signals -->
      <section class="card">
        <h2>Signals del Store</h2>
        
        <sca-alert variant="info" style="margin-bottom: 1.5rem;">
          <sca-icon icon="info" size="24px" [weight]="300" left />
          Todas las propiedades reactivas del SessionStore
        </sca-alert>
        
        <div class="utilities-grid">
          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="code" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>instanceId$()</p>
            <p scaCardWorkshopAddress>
              <sca-badge variant="default">string</sca-badge><br>
              ID único de esta instancia del store
            </p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="event_note" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>lastProcessedEvent$()</p>
            <p scaCardWorkshopAddress>
              <sca-badge variant="default">string | null</sca-badge><br>
              Último evento procesado (ID)
            </p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="hourglass_empty" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>isProcessingExternalEvent$()</p>
            <p scaCardWorkshopAddress>
              <sca-badge variant="default">boolean</sca-badge><br>
              Procesando evento de otra tab
            </p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="share" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>delegateSession$()</p>
            <p scaCardWorkshopAddress>
              <sca-badge variant="default">boolean</sca-badge><br>
              Delegar sesión a otras tabs
            </p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="directions" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>phase$()</p>
            <p scaCardWorkshopAddress>
              <sca-badge variant="default">CommonPhase | StoragePhase</sca-badge><br>
              Fase actual (IDLE, LOADING, LOADED, ERROR, ACTIVE)
            </p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="error_outline" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>error$()</p>
            <p scaCardWorkshopAddress>
              <sca-badge variant="default">unknown | null</sca-badge><br>
              Error si fase es ERROR
            </p>
          </sca-card-workshop>
        </div>
      </section>

      <!-- Métodos -->
      <section class="card">
        <h2>Métodos Principales</h2>
        
        <sca-alert variant="info" style="margin-bottom: 1.5rem;">
          <sca-icon icon="info" size="24px" [weight]="300" left />
          API pública del SessionStore
        </sca-alert>
        
        <div class="utilities-grid">
          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="play_arrow" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>startStorageSync()</p>
            <p scaCardWorkshopAddress>Inicia la sincronización de sesión entre tabs</p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="stop" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>stopStorageSync()</p>
            <p scaCardWorkshopAddress>Detiene la sincronización de sesión</p>
          </sca-card-workshop>

          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              <sca-icon icon="broadcast_on_personal" size="24" [weight]="400" />
            </header>
            <p scaCardWorkshopTitle>broadcastEvent(event)</p>
            <p scaCardWorkshopAddress>Envía un evento de sesión a todas las tabs</p>
          </sca-card-workshop>
        </div>
      </section>

      <!-- Arquitectura -->
      <section class="card">
        <h2>Arquitectura de Sincronización</h2>
        <sca-alert variant="info">
          <strong>🏗️ Componentes de Sincronización:</strong>
          <ol>
            <li><strong>Native Storage Patch:</strong> Intercepta operaciones en storage para detectar cambios de sesión</li>
            <li><strong>IndexedDB Patch:</strong> Detecta cambios en IndexedDB relacionados con sesión</li>
            <li><strong>Cookie Manager:</strong> Gestiona cookies de sincronización de sesión</li>
            <li><strong>Event Broadcaster:</strong> Publica eventos de sesión (logout, token renovado) a otras tabs</li>
            <li><strong>Security Integration:</strong> Coordina autenticación con SecurityStore</li>
          </ol>
        </sca-alert>

        <sca-view-more>
          <h3>🔄 Flujo de Sincronización de Sesión</h3>
          <ol>
            <li>Tab A: Usuario hace logout → evento de sesión generado</li>
            <li>Tab A: Actualiza cookie de sincronización con evento + timestamp</li>
            <li>Tab B: Detecta cambio en cookie (polling cada 1s)</li>
            <li>Tab B: Lee evento de sesión desde cookie/storage</li>
            <li>Tab B: Ejecuta logout automáticamente para mantener consistencia</li>
          </ol>
          <p><strong>Caso de uso principal:</strong> Cuando un usuario cierra sesión en una pestaña, todas las demás pestañas deben cerrarse automáticamente.</p>
        </sca-view-more>
      </section>

      <!-- SSR Safe -->
      <section class="card">
        <h2>SSR-Safe Implementation</h2>
        <sca-alert variant="success">
          <strong>🛡️ Protección SSR:</strong><br>
          El store utiliza <code>isPlatformServer()</code> para:
          <ul>
            <li>Solo ejecutar patches en navegador</li>
            <li>Evitar errores de <code>window</code>, <code>localStorage</code> en SSR</li>
            <li>Compatibilidad total con Angular Universal</li>
          </ul>
        </sca-alert>
      </section>
    </div>
  `,
  styles: [`
    .container { padding: 0 2rem; margin: 0 auto;     @media (max-width: 1400px) {
        padding-left: 0 !important;
        padding-right: 0 !important;
    }}
    .title-test{ margin-bottom: 2rem; }
    .card { background: white; border-radius: 8px; padding: 2rem; margin-bottom: 2rem; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
    .card h2 { color: #333; margin-bottom: 1.5rem; padding-bottom: 0.5rem;}
    .utilities-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1.5rem; margin: 1.5rem 0; }
    pre.code-example { background: #263238; color: #aed581; padding: 0; border-radius: 8px; overflow-x: auto; line-height: 1.6; font-family: 'Courier New', monospace; margin: 0; }
    sca-alert ol, sca-alert ul { padding-left: 1.5rem; margin: 0.5rem 0; }
    sca-alert li { margin: 0.5rem 0; }
  `]
})
export class SessionTestComponent {
  sessionStore = inject(SessionStore);
}



